import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Icon from '../components/ui/Icon.jsx'
import Button from '../components/ui/Button.jsx'
import { Modal } from '../components/ui/Modal.jsx'
import { Field, Input } from '../components/ui/Field.jsx'
import { useFeedback } from '../components/ui/feedback.jsx'
import { useCompany } from '../context/CompanyContext.jsx'
import api, { COMPANY_KEY } from '../lib/api.js'
import { isValidBelgianVat } from '../lib/validation.js'

/*
 * Topbar — DESIGN.md §10 / README C. Page title left; right side: language
 * selector (FR/EN/NL), notifications bell, company selector, and a contextual
 * primary action (passed by each page via the `action` prop).
 */
const LANGS = ['fr', 'en', 'nl']

function LanguagePicker() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false)
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const current = (i18n.resolvedLanguage || i18n.language || 'fr').slice(0, 2)
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 12px',
          borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-alt)',
          background: '#fff', color: 'var(--color-text)', fontSize: 13, fontWeight: 600,
        }}
      >
        <Icon name="globe" size={17} />
        {current.toUpperCase()}
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 46, zIndex: 60, minWidth: 120,
          background: '#fff', border: '1px solid var(--color-border)', borderRadius: 12,
          boxShadow: 'var(--shadow-card)', padding: 6, animation: 'vpop .15s ease',
        }}>
          {LANGS.map((lng) => (
            <button
              key={lng}
              onClick={() => { i18n.changeLanguage(lng); setOpen(false) }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                padding: '8px 10px', borderRadius: 8, border: 'none', background: lng === current ? 'var(--color-bg-lilac-strong)' : 'transparent',
                color: 'var(--color-ink)', fontSize: 13, fontWeight: 600,
              }}
            >
              {lng.toUpperCase()}
              {lng === current && <Icon name="check" size={15} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [unread, setUnread] = useState(0)
  const ref = useRef(null)

  const load = useCallback(() => {
    api.get('/notifications').then(({ data }) => { setItems(data?.notifications || []); setUnread(data?.unread_count || 0) }).catch(() => {})
  }, [])
  useEffect(() => {
    load()
    const id = setInterval(load, 60000) // poll once a minute
    return () => clearInterval(id)
  }, [load])
  useEffect(() => {
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false)
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const openItem = async (n) => {
    setOpen(false)
    if (!n.is_read) { api.post(`/notifications/${n.id}/read`).then(load).catch(() => {}) }
    if (n.action_url) navigate(n.action_url)
  }
  const markAll = () => { api.post('/notifications/read-all').then(load).catch(() => {}) }
  const remove = (n, e) => { e.stopPropagation(); api.delete(`/notifications/${n.id}`).then(load).catch(() => {}) }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button title={t('nav.notifications')} aria-label={t('nav.notifications')} onClick={() => { setOpen((o) => !o); if (!open) load() }} style={{
        position: 'relative', width: 40, height: 40, borderRadius: 'var(--radius-field)',
        border: '1.5px solid var(--color-border-alt)', background: '#fff',
        color: 'var(--color-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }}>
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: 'var(--color-danger)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 46, zIndex: 60, width: 340, background: '#fff', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: 'var(--shadow-card)', animation: 'vpop .15s ease', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--color-border-row)' }}>
            <span style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 14 }}>{t('notifications.title')}</span>
            {unread > 0 && <button onClick={markAll} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>{t('notifications.markAll')}</button>}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>{t('notifications.empty')}</div>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderTop: '1px solid var(--color-border-row)', background: n.is_read ? '#fff' : 'var(--color-bg-lilac)', cursor: n.action_url ? 'pointer' : 'default' }}>
                {!n.is_read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-primary)', marginTop: 6, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-ink)', fontSize: 13.5 }}>{n.title}</div>
                  {n.message && <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 2 }}>{n.message}</div>}
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 3 }}>{fmtAgo(n.created_at)}</div>
                </div>
                <span role="button" tabIndex={0} aria-label={t('common.delete')} onClick={(e) => remove(n, e)} style={{ color: 'var(--color-text-muted)', display: 'inline-flex', flexShrink: 0, padding: 2, cursor: 'pointer' }}><Icon name="x" size={14} /></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function fmtAgo(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return `${d.toLocaleDateString('fr-BE')} ${d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}`
}

function CompanyPicker() {
  const { t } = useTranslation()
  const { companies, currentCompanyId, switchCompany, getCurrentCompany } = useCompany()
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false)
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  if (!companies.length) return null
  const current = getCurrentCompany()
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 14px',
        borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-alt)',
        background: '#fff', color: 'var(--color-ink)', fontSize: 13, fontWeight: 600, maxWidth: 240, cursor: 'pointer',
      }}>
        <Icon name="building" size={16} />
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{current?.name}</span>
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 46, zIndex: 60, minWidth: 240,
          background: '#fff', border: '1px solid var(--color-border)', borderRadius: 12,
          boxShadow: 'var(--shadow-card)', padding: 6, animation: 'vpop .15s ease',
        }}>
          {companies.map((c) => (
            <button key={c.id} onClick={() => { setOpen(false); if (c.id !== currentCompanyId) switchCompany(c.id) }} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
              padding: '9px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: c.id === currentCompanyId ? 'var(--color-bg-lilac-strong)' : 'transparent',
              color: 'var(--color-ink)', fontSize: 13, fontWeight: 600, textAlign: 'left',
            }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
              {c.id === currentCompanyId && <Icon name="check" size={15} />}
            </button>
          ))}
          <div style={{ height: 1, background: 'var(--color-border-row)', margin: '6px 4px' }} />
          <button onClick={() => { setOpen(false); setAdding(true) }} style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 10px',
            borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--color-primary)',
            fontSize: 13, fontWeight: 600, textAlign: 'left', cursor: 'pointer',
          }}>
            <Icon name="plus" size={15} /> {t('company.add')}
          </button>
        </div>
      )}
      {adding && <AddCompanyModal onClose={() => setAdding(false)} />}
    </div>
  )
}

function AddCompanyModal({ onClose }) {
  const { t } = useTranslation()
  const { notify } = useFeedback()
  const [f, setF] = useState({ name: '', vat_digits: '', non_vat_liable: false })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const valid = f.name.trim() && isValidBelgianVat(`BE${f.vat_digits}`) // format + chiffres de contrôle (mod 97)

  const submit = async (e) => {
    e.preventDefault()
    if (!valid) return
    setBusy(true)
    try {
      // The backend creates the company as pending_validation with an onboarding
      // at 'pending_plan'. Switch to it and resume the registration wizard (plan →
      // payment → documents → finalize → validation) — it isn't usable until validated.
      const { data } = await api.post('/companies/create-additional', { name: f.name.trim(), vat_number: `BE${f.vat_digits}`, non_vat_liable: f.non_vat_liable })
      const newId = data?.company_id || (data?.company || data)?.id
      if (newId) {
        localStorage.setItem(COMPANY_KEY, String(newId))
        window.location.assign('/register') // resumes the new company's onboarding from /register/status
      } else { onClose() }
    } catch (err) { notify({ tone: 'error', title: t('company.error'), message: err?.response?.data?.error || t('settings.errorGeneric') }); setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} maxWidth={440} closeOnBackdrop={false}>
      <form onSubmit={submit} style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{t('company.add')}</h3>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>{t('company.addHint')}</p>
        <Field label={t('admin.companies.name')} required><Input value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus required /></Field>
        <Field label={f.non_vat_liable ? t('parties.fields.companyNumber') : t('parties.fields.vatNumber')} required>
          <div style={{ display: 'flex' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 14px', background: 'var(--color-bg-lilac-strong)', border: '1.5px solid var(--color-border-strong)', borderRight: 'none', borderRadius: 'var(--radius-field) 0 0 var(--radius-field)', fontWeight: 700, color: 'var(--color-ink)' }}>BE</span>
            <Input value={f.vat_digits} onChange={(e) => set('vat_digits', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="0123456789" inputMode="numeric" style={{ borderRadius: '0 var(--radius-field) var(--radius-field) 0' }} required />
          </div>
        </Field>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, marginBottom: 18 }}>
          <input type="checkbox" checked={f.non_vat_liable} onChange={(e) => set('non_vat_liable', e.target.checked)} /> {t('parties.fields.nonVatLiable')}
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon="plus" loading={busy} disabled={!valid}>{t('company.create')}</Button>
        </div>
      </form>
    </Modal>
  )
}

export default function Topbar({ title, action }) {
  const { t } = useTranslation()
  return (
    <header style={{
      height: 'var(--topbar-height)', flex: 'none',
      position: 'sticky', top: 0, zIndex: 40,
      background: 'rgba(246,244,251,.86)', backdropFilter: 'blur(10px)',
      borderBottom: '1px solid var(--color-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 28px',
    }}>
      <h1 style={{ fontFamily: 'var(--font-title)', fontSize: 21, fontWeight: 700, color: 'var(--color-ink)' }}>
        {title}
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <LanguagePicker />
        <NotificationBell />
        <CompanyPicker />
        {action}
      </div>
    </header>
  )
}
